import React, { useCallback, useMemo, useRef } from 'react'
import { GetElementType } from 'types'
import ReportElementSettings from './ReportElementSettings'
import getCohortData from 'util/getCohortData'
import {
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableBody,
  Typography,
  Box,
  Collapse,
  alpha,
} from '@mui/material'
import useIsHorizontalOverflow from 'hooks/useIsHorizontalOverflow'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import useExport from 'components/TableElement/useExport'
import useReportElementChart from 'hooks/useReportElementChart'
import ReportElementFloatingLegend from './ReportElementFloatingLegend'
import CopyRowButton from 'components/TableElement/CopyRowButton'
import useElementCohortsAndSubgroups from 'hooks/useElementCohortsAndSubgroups'
import { getCohortToUse, getSubgroupToUse } from 'util/getBarChartData'
import { ReportElementProps } from './ReportElement'
import ReportElementSortButton from './ReportElementSortButton'
import TableElementTableCell from 'components/TableElement/TableCell'
import ReportElementTablePrint from 'components/TableElement/Print'

const Opacity = 0.08
const ColorAsPattern = '#FF0000'

export type TableColumn<R> = {
  key: string
  sortIndex?: number
  title?: React.ReactNode
  render?: (row: R) => React.ReactNode
  renderExport?: (row: R) => string
  color: (row: R) => { backgroundColor?: string; background?: string }
}

interface ElementData {
  type: string
  category_label: string
  categories: string[]
  current: Array<{ name: string; data: string[] }>
  comparative: Array<{ name: string; data: string[] }>
  past_results: Array<{ name: string; data: string[] }>
  cohorts: any
  segmentations: any
  share: boolean
}

const ReportElementTable: React.FC<ReportElementProps> = ({
  reportId,
  element,
  sectionId,
  report,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)

  const {
    config,
    cohort,
    subgroup,
    pastResults,
    parsedData,
    otherReportDetails,
    parsedContent,
    hidden,
    handleSetHidden,
    handleSetSort,
    elementTitle,
  } = useReportElementChart(element, reportId)

  const sort =
    config && config.sort ? config.sort : { value: null, direction: 'asc' }

  try {
    const [elementCohorts, elementSubgroups] = useElementCohortsAndSubgroups(
      element,
      parsedData
    )

    const cohortToUse: string = getCohortToUse(cohort || '', elementCohorts)
    const subgroupToUse: string = getSubgroupToUse(
      subgroup || '',
      elementSubgroups
    )

    const legendItems = Object.keys(parsedContent.color_legend || {}).map(
      (key, i) => {
        const color = `#${key.split('x')[1]}` || ''
        const name = parsedContent.color_legend[key] || ''
        if (color === ColorAsPattern) {
          return {
            color: {
              pattern: { backgroundColor: color },
            },
            name: name,
          }
        }
        return { color, name }
      }
    )
    const title = elementTitle as string
    const exportTable = useExport({ title })

    const handleExport = useCallback(() => {
      if (tableRef.current) exportTable(tableRef.current)
    }, [exportTable])

    const [rows, columns, renderTable] = useMemo(() => {
      try {
        const elementData: ElementData = parsedContent
        const cohortData = getCohortData({
          cohortName: cohortToUse || undefined,
          cohorts: elementData.cohorts,
          cohortSynonyms: parsedData.cohortSynonyms,
        })

        const isSubgroup = parsedContent.data_toggles === 'Subgroup'
        const segmentation = (elementData.segmentations || []).find(
          (s: { name: string }) => s.name === subgroupToUse
        )
        let renderTable = false

        const rows = elementData.categories.map(
          (category: string, i: number) => {
            const order = i
            if (isSubgroup) {
              const segmentationRows =
                segmentation?.series.reduce(
                  (
                    a: any,
                    v: { name: string; data: []; colors: [] },
                    i2: number
                  ) => ({
                    ...a,
                    [`${v.name} ${i2}`]: {
                      value: v.data[i],
                      color: v.colors ? v.colors[i] : 'white',
                    },
                  }),
                  {}
                ) || {}
              return {
                category,
                order,
                ...segmentationRows,
              }
            }
            return {
              category,
              order,
              current: elementData.current.map((item) => item.data[i]) || '',
              pastResults:
                elementData.past_results.map((item) => item.data[i]) || '',
              comparative:
                elementData.comparative.map((item) => item.data[i]) || '',
              cohort: cohortData?.data[i] || '',
            }
          }
        )

        const columns: TableColumn<GetElementType<typeof rows>>[] = [
          {
            key: elementData.category_label || 'category_label',
            title: elementData.category_label || 'Category Label',
            render: (row) => (
              <Box dangerouslySetInnerHTML={{ __html: row.category || '' }} />
            ),
            color: (row) => {
              return {}
            },
          },
        ]

        if (isSubgroup) {
          renderTable = true
          segmentation?.series.forEach((item: { name: string }, i: number) => {
            columns.push({
              key: `${item.name} ${i}` || `segmentation_${i}`,
              title: item.name || `Segmentation ${i}`,
              color: (row) => {
                const r = row[`${item.name} ${i}`]
                const color = `#${r.color.split('x')[1]}`
                if (color === ColorAsPattern) {
                  return {
                    background: `repeating-linear-gradient(45deg, ${alpha(
                      color,
                      Opacity
                    )}, ${alpha(
                      color,
                      Opacity
                    )} 4px, #FFFFFF 4px, #FFFFFF 5px)`,
                  }
                }
                return {
                  backgroundColor:
                    color != 'white' ? alpha(color, Opacity) : '#ffffff',
                }
              },
              render: (row) => row[`${item.name} ${i}`].value,
            })
          })
        } else {
          renderTable =
            elementData.current.length > 0 ||
            elementData.past_results.length > 0 ||
            elementData.comparative.length > 0 ||
            Boolean(cohortData)

          elementData.current.forEach((item, i) => {
            columns.push({
              key: item.name || `current_${i}`,
              title: item.name || `Current ${i}`,
              render: (row) => row.current[i],
              color: (row) => {
                return {}
              },
            })
          })

          if (pastResults && elementData.past_results.length) {
            elementData.past_results.forEach((pastResult, i) => {
              columns.push({
                key: pastResult.name || `past_results_${i}`,
                title: pastResult.name || `Past Results ${i}`,
                render: (row) => row.pastResults[i],
                color: (row) => {
                  return {}
                },
              })
            })
          }

          elementData.comparative.forEach((item, i) => {
            columns.push({
              key: item.name || `comparative_${i}`,
              title: item.name || `Comparative ${i}`,
              render: (row) => row.comparative[i],
              color: (row) => {
                return {}
              },
            })
          })

          if (cohortData) {
            columns.push({
              key: cohortData?.name || 'cohort',
              title: cohortData?.name || 'Cohort',
              render: (row) => row.cohort,
              color: (row) => {
                return {}
              },
            })
          }
        }

        if (elementData.share && !print) {
          columns.push({
            key: 'share',
            title: 'Share',
            render: (row) => {
              return (
                <CopyRowButton
                  row={row}
                  schoolName={otherReportDetails.schoolName}
                  surveyType={otherReportDetails.surveyType}
                />
              )
            },
            color: (row) => ({}),
          })
        }

        return [rows, columns, renderTable]
      } catch (err) {
        return [[], [], false]
      }
    }, [parsedData, parsedContent, cohort, pastResults, subgroup])

    columns.forEach((c, i) => (c.sortIndex = i))

    const shownColumns = useMemo(
      () =>
        columns.filter(
          (c) => !hidden.includes(c.title) && !hidden.includes(c.key)
        ),
      [columns, hidden]
    )

    const isOverflow = useIsHorizontalOverflow(tableContainerRef, [columns])

    if (!rows.length || !renderTable) {
      return null
    }

    rows.sort((a, b) => {
      const column = shownColumns.filter(
        (col) => col.sortIndex === sort.value
      )[0]
      if (column) {
        const rowA = parseInt(
          ((column.render ? column.render(a) : rows.indexOf(a)) as string) ||
            '1'
        )
        const rowB = parseInt(
          ((column.render ? column.render(b) : rows.indexOf(b)) as string) ||
            '1'
        )
        return sort.direction === 'asc'
          ? rowA < rowB
            ? -1
            : 1
          : rowA < rowB
            ? 1
            : -1
      }
      return a.order - b.order
    })

    if (print) {
      return (
        <Box sx={{ width: '100%', mb: 4 }}>
          <ReportElementTablePrint
            cols={columns}
            rows={rows}
            elementTitle={elementTitle}
            legendItems={legendItems}
            print={print}
            reportId={reportId}
            element={element}
            sectionId={sectionId}
            report={report}
            noSubgroup={parsedContent.data_toggles === 'Overall'}
            noCohort={parsedContent.data_toggles === 'Subgroup'}
            noPastResults={parsedContent.data_toggles === 'Subgroup'}
            showHideOptions={
              parsedContent.data_toggles === 'Subgroup'
                ? columns.slice(1)
                : columns.slice(1, parsedContent.share ? -2 : -1)
            }
            hidden={hidden}
            onChangeHidden={handleSetHidden}
            onExport={handleExport}
            favorites={favorites}
            onUpdateFavorites={onUpdateFavorites}
          />
        </Box>
      )
    }

    return (
      <Box sx={{ width: '100%', mb: 4 }}>
        <Box px={4}>
          {elementTitle && <Typography variant="h5">{elementTitle}</Typography>}
        </Box>
        <Box my={2}>
          <ReportElementSettings
            print={print}
            reportId={reportId}
            element={element}
            sectionId={sectionId}
            report={report}
            noSubgroup={parsedContent.data_toggles === 'Overall'}
            noCohort={parsedContent.data_toggles === 'Subgroup'}
            noPastResults={parsedContent.data_toggles === 'Subgroup'}
            showHideOptions={
              parsedContent.data_toggles === 'Subgroup'
                ? columns.slice(1)
                : columns.slice(1, parsedContent.share ? -2 : -1)
            }
            hidden={hidden}
            onChangeHidden={handleSetHidden}
            onExport={handleExport}
            favorites={favorites}
            onUpdateFavorites={onUpdateFavorites}
          />
        </Box>
        <Box px={0} pb={4}>
          <Box
            sx={{ position: 'relative', maxWidth: '100%', overflow: 'hidden' }}
          >
            <TableContainer ref={tableContainerRef}>
              <Table ref={tableRef}>
                <TableHead>
                  {!print && (
                    <TableRow>
                      {shownColumns.map(({ key, sortIndex }, columnIndex) => (
                        <TableElementTableCell
                          key={`sort-${key}`}
                          sortHeader
                          columnIndex={columnIndex}
                          columnLength={shownColumns.length}
                        >
                          {columnIndex !== 0 && key !== 'share' && (
                            <Box
                              sx={{
                                position: 'relative',
                                zIndex: 0,
                                fontSize: '10px',
                              }}
                              display="flex"
                              alignItems="center"
                              m="0 auto"
                            >
                              <ReportElementSortButton
                                onSort={handleSetSort}
                                sortValue={sortIndex || columnIndex}
                                currentSort={
                                  config?.sort
                                    ? config.sort
                                    : { value: null, direction: 'asc' }
                                }
                              />
                            </Box>
                          )}
                        </TableElementTableCell>
                      ))}
                    </TableRow>
                  )}
                  <TableRow>
                    {shownColumns.map(({ key, title }, columnIndex) => (
                      <TableElementTableCell
                        key={key}
                        columnIndex={columnIndex}
                        columnLength={shownColumns.length}
                        header
                      >
                        <strong>{title || key}</strong>
                      </TableElementTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => {
                    const rowId = i
                    return (
                      <TableRow key={rowId}>
                        {shownColumns.map(
                          ({ key, render, color }, columnIndex) => (
                            <TableElementTableCell
                              key={`${rowId}:${key}`}
                              columnLength={shownColumns.length}
                              columnIndex={columnIndex}
                              color={color(row)}
                              tableBodyCell
                            >
                              {render ? render(row) : row[key]}
                            </TableElementTableCell>
                          )
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {isOverflow && (
              <Box
                sx={{
                  backgroundImage:
                    'linear-gradient(to right, rgba(255, 255, 255, 0), white)',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  width: 62,
                }}
              ></Box>
            )}
          </Box>
          {!isOverflow && legendItems.length > 0 && (
            <Box pl={2}>
              <ReportElementFloatingLegend
                removeAxisPadding
                items={legendItems}
                hidden={hidden}
                opacity={Opacity}
                swatchWidth={60}
                swatchHeight={30}
              />
            </Box>
          )}
          <Collapse in={isOverflow}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box pl={2}>
                <ReportElementFloatingLegend
                  removeAxisPadding
                  items={legendItems}
                  hidden={hidden}
                  opacity={Opacity}
                  swatchWidth={60}
                  swatchHeight={30}
                />
              </Box>
              <Box pr={4} display="flex" alignItems="center">
                <Typography
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontSize: '0.8rem',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    paddingRight: 1,
                  }}
                >
                  Scroll table to see additional columns
                </Typography>
                <ArrowForwardIcon fontSize="inherit" />
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    )
  } catch (error) {
    console.error(error)
    return null
  }
}

export default ReportElementTable
